import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Page } from '..'
import Loading from '../../components/loading'
import { NavInExperience } from '../../components/nav'
import { resources as resourcesApi } from '../../lib/api'
import * as api from '../../lib/api'
import { Modal } from '../../components/modal'

// ─── Constantes de module ─────────────────────────────────────────────────────

const TYPE_LABELS = { image: 'Image', audio: 'Audio', video: 'Vidéo', mapground: 'Carte', other: 'Autre' }
const NON_OTHER_TYPES = ['image', 'audio', 'video', 'mapground']
const TYPE_FILTER_KEYS = new Set(['image', 'audio', 'video', 'other'])

const FILTER_OPTIONS = [
  { key: 'image', label: 'Images' },
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Vidéo' },
  { key: 'other', label: 'Autres' },
  { key: 'unused', label: 'Non utilisées', needsUsageMap: true },
]

const SORT_OPTIONS = [
  { key: 'name-asc', label: 'Nom A→Z' },
  { key: 'name-desc', label: 'Nom Z→A' },
  { key: 'date-desc', label: 'Date récente' },
  { key: 'date-asc', label: 'Date ancienne' },
  { key: 'usage-desc', label: 'Plus utilisées' },
  { key: 'usage-asc', label: 'Moins utilisées' },
]

// ─── Fonctions utilitaires ────────────────────────────────────────────────────

const formatSize = bytes => {
  if (!bytes) return ''
  if (bytes < 1024) return bytes + ' o'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' Ko'
  return (Math.round(bytes / 1024 / 1024 * 10) / 10) + ' Mo'
}

const formatDate = dateStr => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const applyFilters = (resources, activeFilters, usageMap) => {
  const activeTypes = new Set([...activeFilters].filter(f => TYPE_FILTER_KEYS.has(f)))
  const showUnused = activeFilters.has('unused')
  return resources.filter(r => {
    const passesType = activeTypes.size === 0 || activeTypes.has(r.type) || (activeTypes.has('other') && !NON_OTHER_TYPES.includes(r.type))
    const passesUnused = !showUnused || (usageMap?.[r._id]?.length === 0)
    return passesType && passesUnused
  })
}

const applySort = (resources, sortBy, usageMap) => [...resources].sort((a, b) => {
  const nameA = (a.name || a._id || '').toLowerCase()
  const nameB = (b.name || b._id || '').toLowerCase()
  const usageCount = r => usageMap?.[r._id]?.length ?? 0
  switch (sortBy) {
    case 'name-desc':  return nameB.localeCompare(nameA)
    case 'date-desc':  return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
    case 'date-asc':   return new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt)
    case 'usage-desc': return usageCount(b) - usageCount(a)
    case 'usage-asc':  return usageCount(a) - usageCount(b)
    default:           return nameA.localeCompare(nameB)
  }
})

/** Trie d'abord, puis groupe en préservant l'ordre : le rang d'un groupe = rang de son meilleur élément. */
const groupResources = (resources, sortBy, usageMap) => {
  const groups = []
  const hashToGroup = {}
  for (const resource of applySort(resources, sortBy, usageMap)) {
    const hash = resource.original?.hash
    if (hash && hashToGroup[hash]) hashToGroup[hash].push(resource)
    else {
      const group = [resource]
      groups.push(group)
      if (hash) hashToGroup[hash] = group
    }
  }
  return groups
}

const buildUsageMap = (resources, modules, points, experience, siteUid) => {
  const resourceIds = new Set(resources.map(r => r._id))
  const usageMap = Object.fromEntries(resources.map(r => [r._id, []]))
  const xpLink = `/site/${siteUid}/experience/${experience._id}`

  const scan = (entityObj, label, link) => {
    const json = JSON.stringify(entityObj)
    resourceIds.forEach(id => { if (json.includes(id)) usageMap[id].push({ label, link }) })
  }

  if (experience.picture && resourceIds.has(experience.picture))
    usageMap[experience.picture].push({ label: 'Expérience (image)', link: xpLink })
  if (experience.splashscreen && resourceIds.has(experience.splashscreen))
    usageMap[experience.splashscreen].push({ label: 'Expérience (splashscreen)', link: xpLink })
  ;(experience.characters || []).forEach(char =>
    scan(char, `Personnage "${char.label || char._id}"`, `${xpLink}/characters/`))
  ;(modules || []).forEach(m =>
    scan(m, `Module "${m.internalName || m.title || m.type}"`, `${xpLink}/scenario/${m.scenarioId}/module/${m._id}`))
  ;(points || []).forEach(p =>
    scan(p, `Point "${p.name || p._id}"`, `${xpLink}/track/${p.trackId}/point/${p._id}`))

  return usageMap
}

// ─── Composant principal ──────────────────────────────────────────────────────

export const ExperienceResources = ({ match }) => {
  const selectAllRef = useRef(null)

  const [resources, setResources] = useState()
  const [trashedResources, setTrashedResources] = useState([])
  const [usageMap, setUsageMap] = useState(null)
  const [activeFilters, setActiveFilters] = useState(new Set())
  const [appliedFilters, setAppliedFilters] = useState(new Set())
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [sortBy, setSortBy] = useState('name-asc')
  const [showTrash, setShowTrash] = useState(false)
  const [groupByOrigin, setGroupByOrigin] = useState(false)
  const [selected, setSelected] = useState(new Set())
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false)

  const { siteUid, experienceUid } = match.params

  // Charge toutes les données et reconstruit la carte d'usages.
  const loadAll = async () => {
    const [allResources, modules, points] = await Promise.all([
      resourcesApi.getFrom('experiences', experienceUid, true),
      api.modules.getFrom('experiences', experienceUid),
      api.points.getFrom('experiences', experienceUid),
    ])
    const experience = await api.experiences.get(experienceUid)
    setResources((allResources || []).filter(r => !r.deleted))
    setTrashedResources((allResources || []).filter(r => r.deleted))
    if (experience) setUsageMap(buildUsageMap(allResources || [], modules, points, experience, siteUid))
  }

  // Chargement initial.
  useEffect(() => { loadAll() }, []) // eslint-disable-line

  // Réinitialise la sélection lors d'un changement de filtres appliqués ou de vue.
  useEffect(() => { setSelected(new Set()) }, [appliedFilters, showTrash])
  // Réinitialise la recherche lors d'un changement de vue.
  useEffect(() => { setSearch(''); setAppliedSearch('') }, [showTrash])
  // Applique les filtres avec un délai de 250ms.
  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters(activeFilters), 250)
    return () => clearTimeout(t)
  }, [activeFilters])
  // Applique la recherche avec un délai de 1s.
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search), 750)
    return () => clearTimeout(t)
  }, [search])

  /// ─── Valeurs dérivées ─────────────────────────────────────────────────────

  const currentList = showTrash ? trashedResources : (resources || [])
  const searchLower = appliedSearch.toLowerCase()
  const filteredOnly = applyFilters(currentList, appliedFilters, usageMap)
    .filter(r => !searchLower || (r.name || r._id || '').toLowerCase().includes(searchLower))
  const groups = groupByOrigin
    ? groupResources(filteredOnly, sortBy, usageMap)
    : applySort(filteredOnly, sortBy, usageMap).map(r => [r])
  const filtered = groups.flat()
  const groupsWithVariants = groups.filter(g => g.length > 1)

  // Met à jour l'état indéterminé du checkbox "tout sélectionner" après chaque rendu.
  useEffect(() => {
    if (!selectAllRef.current) return
    const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r._id))
    selectAllRef.current.checked = allSelected
    selectAllRef.current.indeterminate = !allSelected && filtered.some(r => selected.has(r._id))
  })

  /// ─── Helpers d'état ──────────────────────────────────────────────────────

  const patchResource = (id, patch) => {
    const fn = r => r._id === id ? { ...r, ...patch } : r
    setResources(prev => prev.map(fn))
    setTrashedResources(prev => prev.map(fn))
  }

  /// ─── Handlers ────────────────────────────────────────────────────────────

  const toggleFilter = key => setActiveFilters(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const toggleOne = useCallback(id => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  }), [])

  const toggleAll = () => {
    const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r._id))
    setSelected(prev => {
      const next = new Set(prev)
      if (allSelected) filtered.forEach(r => next.delete(r._id))
      else filtered.forEach(r => next.add(r._id))
      return next
    })
  }

  const confirmDelete = async () => {
    const resource = pendingDelete
    setPendingDelete(null)
    await resourcesApi.remove(resource._id)
    setResources(prev => prev.filter(r => r._id !== resource._id))
    setTrashedResources(prev => [...prev, { ...resource, deleted: true }])
    setSelected(prev => { const next = new Set(prev); next.delete(resource._id); return next })
  }

  const confirmBulkDelete = async () => {
    setPendingBulkDelete(false)
    const ids = [...selected].filter(id => resources.some(r => r._id === id))
    for (const id of ids) await resourcesApi.remove(id)
    const moved = ids.map(id => resources.find(r => r._id === id)).filter(Boolean)
    setResources(prev => prev.filter(r => !ids.includes(r._id)))
    setTrashedResources(prev => [...prev, ...moved.map(r => ({ ...r, deleted: true }))])
    setSelected(new Set())
  }

  const restoreResource = useCallback(async resource => {
    await api.resources.update(resource._id, { deleted: false })
    setTrashedResources(prev => prev.filter(r => r._id !== resource._id))
    setResources(prev => [...prev, { ...resource, deleted: false }])
  }, [])

  const confirmBulkRestore = async () => {
    const ids = [...selected].filter(id => trashedResources.some(r => r._id === id))
    for (const id of ids) await api.resources.update(id, { deleted: false })
    const restored = ids.map(id => trashedResources.find(r => r._id === id)).filter(Boolean)
    setTrashedResources(prev => prev.filter(r => !ids.includes(r._id)))
    setResources(prev => [...prev, ...restored.map(r => ({ ...r, deleted: false }))])
    setSelected(new Set())
  }

  const renameResource = async (id, newName) => {
    const updated = await api.resources.update(id, { name: newName })
    patchResource(id, updated || { name: newName })
  }

  /// ─── Rendu ───────────────────────────────────────────────────────────────

  return (
    <Page>
      <style>{RESOURCE_ROW_CSS}</style>
      <NavInExperience />
      <div id="content">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 style={{ margin: 0, flex: 1 }}>{showTrash ? 'Corbeille' : 'Ressources'}</h3>
          <button className={`btn btn-sm btn-${groupByOrigin ? 'primary' : 'default'}`} onClick={() => setGroupByOrigin(v => !v)} title="Regrouper les ressources issues de la même source">
            <i className="fa fa-object-group" /> Grouper par origine
          </button>
          <button className={`btn btn-sm btn-${showTrash ? 'default' : 'warning'}`} onClick={() => setShowTrash(v => !v)}>
            {showTrash ? 'Retour' : `Corbeille${trashedResources.length > 0 ? ` (${trashedResources.length})` : ''}`}
          </button>
        </div>

        <input
          type="search"
          className="form-control"
          placeholder="Rechercher par nom…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <FilterBar activeFilters={activeFilters} onToggle={toggleFilter} usageMap={usageMap} />

        {!resources ? <Loading /> : (
          <>
            <p style={{ color: '#888', fontSize: 13, margin: '0 0 8px 0' }}>
              {filtered.length} ressource{filtered.length !== 1 ? 's' : ''}
              {activeFilters.size > 0 ? ' (filtrées)' : ''}
              {groupsWithVariants.length > 0 ? ` · ${groupsWithVariants.length} groupe${groupsWithVariants.length > 1 ? 's' : ''} de variantes` : ''}
            </p>

            {filtered.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0 8px 0', borderBottom: '2px solid #ddd', marginBottom: 4 }}>
                <input type="checkbox" ref={selectAllRef} onChange={toggleAll} style={{ cursor: 'pointer' }} title="Tout sélectionner / désélectionner" />
                <span style={{ fontSize: 12, color: '#888', flex: 1 }}>
                  {selected.size > 0 ? `${selected.size} sélectionnée${selected.size > 1 ? 's' : ''}` : 'Tout sélectionner'}
                </span>
                <select className="form-control input-sm" value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ width: 'auto' }}>
                  {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
            )}

            {filtered.length === 0
              ? <p style={{ color: '#999' }}>{showTrash ? 'La corbeille est vide' : 'Aucune ressource'}</p>
              : groups.map(group => (
                <div key={group[0]._id} style={{ marginBottom: group.length > 1 ? 8 : 0 }}>
                  {group.length > 1 && (
                    <div style={{ fontSize: 11, color: '#888', padding: '6px 0 2px', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <i className="fa fa-clone" /> {group.length} variantes · même origine
                    </div>
                  )}
                  <div style={group.length > 1 ? { borderLeft: '3px solid #ddd', paddingLeft: 10 } : {}}>
                    {group.map(resource => (
                      <ResourceRow
                        key={resource._id}
                        resource={resource}
                        usages={usageMap ? usageMap[resource._id] ?? null : null}
                        checked={selected.has(resource._id)}
                        onToggle={toggleOne}
                        onDelete={setPendingDelete}
                        isTrash={showTrash}
                        onRestore={restoreResource}
                        onRename={renameResource}
                      />
                    ))}
                  </div>
                </div>
              ))
            }
          </>
        )}
      </div>

      {/* Modal suppression → corbeille */}
      <Modal isOpen={!!pendingDelete} close={() => setPendingDelete(null)} height="40vh">
        {pendingDelete && (
          <div>
            <h4>Mettre à la corbeille</h4>
            <p>Déplacer <strong>{pendingDelete.name}</strong> dans la corbeille ?</p>
            <UsageWarning id={pendingDelete._id} usageMap={usageMap} message="Sa suppression laissera des images manquantes." />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-danger" onClick={confirmDelete}><i className="fa fa-trash-o" /> Mettre à la corbeille</button>
              <button className="btn btn-default" onClick={() => setPendingDelete(null)}>Annuler</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal suppression groupée → corbeille */}
      <Modal isOpen={pendingBulkDelete} close={() => setPendingBulkDelete(false)} height="60vh">
        {pendingBulkDelete && (
          <div>
            <h4>Mettre {selected.size} ressource{selected.size > 1 ? 's' : ''} à la corbeille ?</h4>
            <BulkList ids={[...selected]} resourceList={resources} usageMap={usageMap} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-danger" onClick={confirmBulkDelete}><i className="fa fa-trash-o" /> Mettre à la corbeille</button>
              <button className="btn btn-default" onClick={() => setPendingBulkDelete(false)}>Annuler</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Boutons flottants */}
      {selected.size > 0 && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', gap: 8 }}>
          {showTrash
            ? <button className="btn btn-default" onClick={confirmBulkRestore} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                <i className="fa fa-undo" /> Restaurer ({selected.size})
              </button>
            : <button className="btn btn-danger" onClick={() => setPendingBulkDelete(true)} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                <i className="fa fa-trash-o" /> Mettre à la corbeille ({selected.size})
              </button>
          }
        </div>
      )}
    </Page>
  )
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

const ResourceThumbnail = ({ resource, size = 60 }) => {
  if (resource.type === 'image' && resource.original) {
    const file = (resource.ld || resource.hd || resource.original).file
    return <img src={resourcesApi.getUrl(file)} alt={resource.name || ''} loading="lazy" style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
  }
  const icons = { audio: 'fa-music', video: 'fa-film', mapground: 'fa-map-o' }
  return (
    <div style={{ width: size, height: size, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', borderRadius: 4 }}>
      <i className={`fa ${icons[resource.type] || 'fa-file-o'}`} style={{ fontSize: size * 0.4, color: '#aaa' }} />
    </div>
  )
}

const UsageBadge = memo(({ usages, expanded, onToggle }) => {
  if (usages === null) return <span style={{ color: '#ccc', fontSize: 12 }}>…</span>
  if (usages.length === 0) return <span className="label label-default" style={{ fontWeight: 'normal', cursor: 'default' }}>Non utilisée</span>
  return (
    <button className="btn btn-xs btn-default" onClick={e => { e.stopPropagation(); onToggle() }} title="Voir les utilisations">
      {usages.length} utilisation{usages.length > 1 ? 's' : ''} <i className={`fa fa-caret-${expanded ? 'up' : 'down'}`} />
    </button>
  )
})

const FilterBar = memo(({ activeFilters, onToggle, usageMap }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 12, alignItems: 'center' }}>
    {FILTER_OPTIONS.map(f => {
      const disabled = f.needsUsageMap && !usageMap
      return (
        <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#bbb' : undefined, userSelect: 'none', margin: 0 }} title={disabled ? 'Analyse des usages en cours…' : undefined}>
          <input type="checkbox" checked={activeFilters.has(f.key)} onChange={() => !disabled && onToggle(f.key)} disabled={disabled} />
          {f.label}
        </label>
      )
    })}
  </div>
))

/** Liste de ressources pour les modals de suppression groupée. */
const BulkList = ({ ids, resourceList, usageMap }) => (
  <div style={{ maxHeight: '35vh', overflowY: 'auto', marginBottom: 12 }}>
    {ids.map(id => {
      const resource = resourceList?.find(r => r._id === id)
      if (!resource) return null
      const usedCount = usageMap?.[id]?.length ?? 0
      return (
        <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid #eee' }}>
          <ResourceThumbnail resource={resource} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resource.name || resource._id}</div>
            {usedCount > 0 && <div style={{ fontSize: 12, color: '#c00' }}><i className="fa fa-exclamation-triangle" /> Utilisée dans {usedCount} endroit{usedCount > 1 ? 's' : ''}</div>}
          </div>
        </div>
      )
    })}
  </div>
)

/** Avertissement d'usage dans les modals de suppression. Rien si non utilisée. */
const UsageWarning = ({ id, usageMap, message, prefix = '' }) => {
  const count = usageMap?.[id]?.length ?? 0
  if (!count) return null
  return (
    <div className="alert alert-danger" style={{ marginBottom: 12 }}>
      <i className="fa fa-exclamation-triangle" />{' '}
      Cette ressource est {prefix}utilisée dans <strong>{count} endroit{count > 1 ? 's' : ''}</strong>. {message}
    </div>
  )
}

const ResourceRow = memo(({ resource, usages, checked, onToggle, onDelete, isTrash, onRestore, onRename }) => {
  const [expanded, setExpanded] = useState(false)
  const [editingName, setEditingName] = useState(null)

  const sizeBytes = resource.hd?.size ?? resource.original?.size
  const metadata = [
    TYPE_LABELS[resource.type] || resource.type,
    resource.original?.width ? `${resource.original.width}×${resource.original.height}` : null,
    formatSize(sizeBytes) || null,
    formatDate(resource.updatedAt || resource.createdAt) || null,
  ].filter(Boolean).join(' · ')

  const commitRename = value => {
    const trimmed = (value || '').trim()
    setEditingName(null)
    if (trimmed && trimmed !== resource.name) onRename(resource._id, trimmed)
  }

  return (
    <div>
      <div className="res-row" style={{ padding: '8px 0', borderBottom: expanded ? 'none' : '1px solid #eee', opacity: isTrash ? 0.75 : 1 }}>
        <div className="res-row__left">
          <input type="checkbox" checked={checked} onChange={() => onToggle(resource._id)} style={{ cursor: 'pointer' }} />
          <ResourceThumbnail resource={resource} />
        </div>

        <div className="res-row__content">
          <div className="res-row__info">
            {editingName !== null ? (
              <input
                autoFocus type="text" className="form-control input-sm" value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={e => commitRename(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(e.target.value); if (e.key === 'Escape') setEditingName(null) }}
              />
            ) : (
              <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }} title="Cliquer pour renommer" onClick={() => setEditingName(resource.name || '')}>
                {resource.name || resource._id}
              </div>
            )}
            <div style={{ fontSize: 12, color: '#999' }}>{metadata}</div>
          </div>

          <div className="res-row__actions">
            <UsageBadge usages={usages} expanded={expanded} onToggle={() => setExpanded(v => !v)} />
            {isTrash
              ? <button className="btn btn-default btn-sm" onClick={() => onRestore(resource)}>Restaurer</button>
              : <button className="btn btn-danger btn-sm" onClick={() => onDelete(resource)} title="Mettre à la corbeille">
                  <i className="fa fa-trash-o" />
                </button>
            }
          </div>
        </div>
      </div>

      {expanded && usages?.length > 0 && (
        <div style={{ padding: '4px 0 8px 0', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {usages.map((usage, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ color: '#666' }}>{usage.label}</span>
              <Link to={usage.link} className="btn btn-default btn-xs" title="Voir"><i className="fa fa-eye" /></Link>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}, (prev, next) =>
  prev.resource === next.resource &&
  prev.usages === next.usages &&
  prev.checked === next.checked &&
  prev.isTrash === next.isTrash
)

// ─── Styles ───────────────────────────────────────────────────────────────────

const RESOURCE_ROW_CSS = `
  .res-row { display: flex; align-items: center; gap: 12px; }
  .res-row__left { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .res-row__content { display: contents; }
  .res-row__info { flex: 1; min-width: 0; }
  .res-row__actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; flex-wrap: wrap; }
  @media (max-width: 640px) {
    .res-row { align-items: flex-start; }
    .res-row__left { padding-top: 4px; }
    .res-row__content { display: flex; flex: 1; min-width: 0; flex-direction: column; gap: 4px; }
    .res-row__actions { flex-shrink: 1; }
  }
`
